import { Component } from '@theme/component';
import { mediaQueryLarge } from '@theme/utilities';
import { DialogOpenEvent, DialogCloseEvent } from '@theme/dialog';
import { ThemeEvents, SlideshowSelectEvent } from '@theme/events';

/**
 * Cart upsell component that displays as an accordion on mobile and sidebar drawer on desktop.
 *
 * @extends {Component}
 */
export class CartUpsell extends Component {
  #controller = new AbortController();
  /** @type {HTMLElement | null} */
  #desktopSidebar = null;
  /** @type {HTMLElement | null} */
  #cartDrawer = null;
  /** @type {NodeListOf<HTMLElement> | null} */
  #mobileDots = null;
  /** @type {NodeListOf<HTMLElement> | null} */
  #desktopDots = null;
  /** @type {NodeListOf<HTMLElement> | null} */
  #promoTexts = null;
  /** @type {HTMLElement | null} */
  #slideshow = null;

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;

    // Find desktop sidebar element
    this.#desktopSidebar = this.querySelector('.cart-upsell__sidebar');
    
    // Find dots and promo text elements
    this.#mobileDots = this.querySelectorAll('.cart-upsell--mobile .cart-upsell__dot');
    this.#desktopDots = this.querySelectorAll('.cart-upsell--desktop .cart-upsell__dot');
    this.#promoTexts = this.querySelectorAll('.cart-upsell__promo-text, .cart-upsell__promo-message .cart-upsell__promo-text');
    this.#slideshow = this.querySelector('slideshow-component[ref="cartUpsellMobile"]');
    
    // Find cart drawer component
    this.#cartDrawer = document.querySelector('cart-drawer-component');

    if (this.#cartDrawer) {
      // Listen for dialog open/close events on cart drawer component
      this.#cartDrawer.addEventListener(DialogOpenEvent.eventName, this.#handleCartDrawerOpen, { signal });
      this.#cartDrawer.addEventListener(DialogCloseEvent.eventName, this.#handleCartDrawerClose, { signal });
    }

    // Listen for cart updates to update promo text
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate, { signal });

    // Handle media query changes
    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange, { signal });

    // Setup slideshow dots sync and click handlers
    if (this.#slideshow) {
      this.#slideshow.addEventListener(SlideshowSelectEvent.eventName, this.#handleSlideSelect, { signal });
      
      // Add click handlers to mobile dots
      if (this.#mobileDots && this.#slideshow) {
        this.#mobileDots.forEach((dot, index) => {
          dot.addEventListener('click', () => {
            const slideshow = /** @type {any} */ (this.#slideshow);
            if (slideshow && typeof slideshow.select === 'function') {
              slideshow.select(index);
            }
          }, { signal });
        });
      }
    }


    // Add click handlers to desktop dots (if needed for future functionality)
    if (this.#desktopDots) {
      this.#desktopDots.forEach((dot, index) => {
        dot.addEventListener('click', () => {
          // Desktop doesn't have carousel, but we can scroll to product if needed
        }, { signal });
      });
    }

    // Initialize sidebar state based on cart drawer state
    this.#updateSidebarState();
    this.#updatePromoText();
  }

  disconnectedCallback() {
    this.#controller.abort();
  }

  /**
   * Handles cart drawer open event.
   */
  #handleCartDrawerOpen = () => {
    this.#updateSidebarState();
    this.#updatePromoText();
  };

  /**
   * Handles cart drawer close event.
   */
  #handleCartDrawerClose = () => {
    this.#updateSidebarState();
  };

  /**
   * Handles media query changes.
   */
  #handleMediaQueryChange = () => {
    this.#updateSidebarState();
  };

  /**
   * Handles cart update event.
   */
  #handleCartUpdate = (event) => {
    if (this.#isMobileDrawerClosed()) return;

    const itemCount = event.detail?.data?.itemCount;
    this.#updatePromoText(itemCount);
  };

  /**
   * Handles slide selection to update dots.
   * @param {SlideshowSelectEvent} event
   */
  #handleSlideSelect = (event) => {
    const slideIndex = event.detail?.index ?? 0;
    this.#updateDots(slideIndex);
  };

  /**
   * Updates dots based on cart count (0, 1, 2, 3+).
   */
  #updateDotsByCartCount(cartCount = this.#getCartCount()) {
    const filledDots = Math.min(cartCount, 3);
    
    const allDots = [
      ...(this.#mobileDots ? Array.from(this.#mobileDots) : []),
      ...(this.#desktopDots ? Array.from(this.#desktopDots) : [])
    ];
    
    allDots.forEach((dot, i) => {
      // Fill dots based on cart count: 0 items = 0 filled, 1 item = 1 filled, 2 items = 2 filled, 3+ items = 3 filled
      const isFilled = i < filledDots;
      dot.setAttribute('aria-selected', String(isFilled));
    });
  }

  /**
   * Updates dots based on current slide (for mobile carousel).
   * @param {number} index
   */
  #updateDots(index) {
    const allDots = [
      ...(this.#mobileDots ? Array.from(this.#mobileDots) : []),
      ...(this.#desktopDots ? Array.from(this.#desktopDots) : [])
    ];
    allDots.forEach((dot, i) => {
      dot.setAttribute('aria-selected', String(i === index));
    });
  }

  /**
   * Gets current cart count.
   * @returns {number}
   */
  #getCartCount() {
    const cartItemCountElement = document.querySelector('[ref="cartItemCount"]');
    return cartItemCountElement ? parseInt(cartItemCountElement.textContent, 10) : 0;
  }

  /**
   * Updates promo text based on cart count.
   */
  #updatePromoText(itemCountFromEvent) {
    if (!this.#promoTexts) return;

    const normalizedItemCount =
      typeof itemCountFromEvent === 'number' ? itemCountFromEvent : parseInt(itemCountFromEvent, 10);
    const cartCount = Number.isFinite(normalizedItemCount) ? normalizedItemCount : this.#getCartCount();
    const promoKey = cartCount === 0 ? 'cart_upsell_promo_0' :
                     cartCount === 1 ? 'cart_upsell_promo_1' :
                     cartCount === 2 ? 'cart_upsell_promo_2' : 'cart_upsell_promo_3';

    // Get translation from Theme.translations
    const promoText = typeof Theme !== 'undefined' && Theme.translations?.[promoKey] || '';

    if (promoText) {
      // Update all promo text elements (including summary text)
      const allPromoTexts = [
        ...Array.from(this.#promoTexts || []),
        ...Array.from(this.querySelectorAll('.cart-upsell__summary-text'))
      ];
      
      allPromoTexts.forEach((textEl) => {
        textEl.textContent = promoText;
        textEl.setAttribute('data-cart-count', String(cartCount));
      });
    }

    // Also update dots based on cart count
    this.#updateDotsByCartCount(cartCount);
  }

  #isMobileDrawerClosed() {
    if (mediaQueryLarge.matches) return false;

    const dialog = this.#cartDrawer?.querySelector('dialog');
    if (!(dialog instanceof HTMLDialogElement)) return true;
    return !dialog.open;
  }

  /**
   * Updates sidebar state based on cart drawer state and screen size.
   */
  #updateSidebarState() {
    // Sidebar is now part of the cart drawer layout, so no need to toggle visibility
    // It will automatically be visible when cart drawer is open since it's inside the dialog
  }

}

if (!customElements.get('cart-upsell-component')) {
  customElements.define('cart-upsell-component', CartUpsell);
}
